/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { JSXInternal } from 'preact/src/jsx';
import { ChartAxisText } from './ChartAxisText';
import { getEnclosingRectDims } from './utils/labelUtils';
import { TextProps } from '#PRIVATE_VisSVGText/text.types';
import { AxisStyles, styles } from './themes/AxisStyles.css';
import { AxisRedwoodTheme } from './themes/redwood/AxisTheme';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { updateVisTextStyleProperties } from '#utils/UNSAFE_visUtils/textUtils';
import { Dimension, VisTextStyle } from '#utils/UNSAFE_visTypes/common';
import { styles as VisStyles } from '#hooks/PRIVATE_useVisDnd/themes/VisDndStyles.css';
import { classNames } from '#utils/UNSAFE_classNames';
import { ColorProps } from '#utils/UNSAFE_interpolations/colors';

type SVGTextAttrs = JSXInternal.HTMLAttributes<SVGTextElement>;

type Props = {
  paths?: [number, number, number, number][];
  isRtl: boolean;
  title?: string;
  activeId?: string;
  titleProps?: TextProps;
  focusedGroupIndex?: number;
  focusedGroupLevelIndex?: number;
  titleStyle: VisTextStyle;
  tickLabelStyle?: SVGTextAttrs['style'];
  tickLabels: (TextProps & { index: number })[];
  isTextInteractive?: (index: number) => boolean;
  axisPosition: 'left' | 'right' | 'top' | 'bottom';
  cursor?: string;
  availSpace?: Dimension;
  dragOverStyle: 'accept' | 'reject' | 'none';
  separators?: [number, number, number, number][];
  separatorColor: ColorProps['color'];
};

/**
 * Renders the chart axis.
 */
export function Axis({
  title,
  titleStyle,
  titleProps,
  tickLabels,
  tickLabelStyle,
  focusedGroupIndex,
  focusedGroupLevelIndex,
  axisPosition,
  isRtl,
  activeId,
  isTextInteractive,
  cursor,
  availSpace,
  dragOverStyle,
  separators,
  separatorColor
}: Props) {
  const { classes } = useComponentTheme<AxisStyles>(AxisRedwoodTheme);
  const axisClass = [classes];
  switch (dragOverStyle) {
    case 'accept':
      axisClass.push(VisStyles.dndDragOverSvgStyle);
      break;
    case 'reject':
      axisClass.push(VisStyles.dndRejectedStyle);
      axisClass.push(styles.axisBackground);
      break;
    case 'none':
      axisClass.push(styles.axisBackground);
  }
  return (
    <>
      <g class={classNames(axisClass)} style={{ cursor }}>
        <rect {...availSpace}></rect>
        {title && titleProps && (
          <ChartAxisText
            textProps={titleProps}
            isRtl={isRtl}
            isRotated={!!titleProps.isRotated}
            style={updateVisTextStyleProperties(titleStyle)}
            isTitle
            isInteractive={false}
            axisPosition={axisPosition}
          />
        )}
        {tickLabels.length > 0 && (
          <g style={tickLabelStyle} class={classNames([styles.axisLabel])}>
            {tickLabels.map(({ index, ...textProps }: Props['tickLabels'][0]) => {
              const level = parseInt(textProps.dataProps?.['data-oj-private-level-index']!);
              const group = parseInt(textProps.dataProps?.['data-oj-private-group-index']!);
              const isFocused = focusedGroupIndex === group && focusedGroupLevelIndex === level;
              return (
                <>
                  {isFocused
                    ? textProps.text &&
                      textProps.dims && (
                        <rect
                          rx="2"
                          ry="2"
                          {...getEnclosingRectDims(textProps.dims, axisPosition, isRtl)}
                          class={styles.outlineRing}></rect>
                      )
                    : ''}
                  {textProps.text && (
                    <ChartAxisText
                      textProps={textProps}
                      isTitle={false}
                      isRotated={!!textProps.isRotated}
                      isRtl={isRtl}
                      dataProps={textProps.dataProps}
                      isInteractive={!!isTextInteractive?.(index)}
                      id={isFocused ? activeId : ''}
                    />
                  )}
                </>
              );
            })}
          </g>
        )}
      </g>
      {separators?.map((path) => {
        return (
          <line
            style={{ stroke: separatorColor }}
            x1={path[0]}
            y1={path[1]}
            x2={path[2]}
            y2={path[3]}
            className={styles.groupSeparatorLine}></line>
        );
      })}
    </>
  );
}
